import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, Mail, Phone, Flame,
  FileText, MessageSquare, History, Calendar, GitMerge,
  Pencil, Check, X, AlertCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import {
  STAGE_LABEL, STAGE_BADGE,
  formatFormType, formatDateTime, formatRelative, isReadOnly,
} from "../utils";
import MergeCompanyDialog from "../components/MergeCompanyDialog";

const formatMoney = (n) => {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
};

const KIND_ICON = {
  submission: FileText,
  note: MessageSquare,
  audit: History,
};

const KIND_LABEL = {
  submission: "Submission",
  note: "Note",
  audit: "Activity",
};

export default function CompanyDetailPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user } = useDashAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  // When the rename endpoint returns HTTP 409 with a suggest_merge
  // payload, we stash it here so the modal can offer "merge into that
  // one" vs "rename anyway" instead of just failing silently.
  const [renameConflict, setRenameConflict] = useState(null);
  const readOnly = isReadOnly(user);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await dashApi.get(`/companies/${encodeURIComponent(key)}`);
      setData(data);
    } catch (e) {
      setErr(e.message || "Failed to load company");
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  const startRename = () => {
    // Placeholders open with an empty field (so the auto-generated
    // "Unnamed — Acct #…" name doesn't have to be manually cleared).
    // Real-name edits pre-fill with the current value.
    setRenameValue(data?.is_name_placeholder ? "" : (data?.company || ""));
    setRenaming(true);
  };

  const cancelRename = () => {
    setRenaming(false);
    setRenameValue("");
  };

  const saveRename = async (e, { force = false } = {}) => {
    e?.preventDefault?.();
    const next = renameValue.trim();
    if (!next) {
      toast.error("Company name can't be empty");
      return;
    }
    if (next === (data?.company || "")) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      const { data: resp } = await dashApi.patch(
        `/companies/${encodeURIComponent(key)}`,
        { name: next },
        { params: force ? { force: true } : undefined },
      );
      toast.success(`Renamed to “${resp.name}”`);
      setRenameConflict(null);
      cancelRename();
      await load();
    } catch (e2) {
      // 409 = duplicate normalized_key → surface merge suggestion
      const detail = e2?.response?.data?.detail;
      if (
        e2?.response?.status === 409
        && detail
        && typeof detail === "object"
        && detail.suggest_merge
      ) {
        setRenameConflict({
          message: detail.message,
          target: detail.suggest_merge,
          attemptedName: next,
        });
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || e2.message || "Rename failed");
        toast.error(msg);
      }
    } finally {
      setRenameSaving(false);
    }
  };

  const mergeIntoConflictTarget = async () => {
    if (!renameConflict?.target?.target_id) return;
    setRenameSaving(true);
    try {
      // POST /companies/{source_id}/merge — folds this company into the
      // existing one and redirects the user there so all subsequent
      // work happens on the surviving record.
      await dashApi.post(
        `/companies/${encodeURIComponent(data.id)}/merge`,
        { target_id: renameConflict.target.target_id },
      );
      toast.success(`Merged into “${renameConflict.target.target_name}”`);
      const slug = renameConflict.target.target_slug || renameConflict.target.target_id;
      setRenameConflict(null);
      navigate(`/dashboard/companies/${slug}`, { replace: true });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || e2.message || "Merge failed");
    } finally {
      setRenameSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (err) {
    return (
      <div className="space-y-3">
        <Link to="/dashboard/companies" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to companies
        </Link>
        <div className="text-rose-700 bg-rose-50 border border-rose-200 p-3 rounded-md">{err}</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="dashboard-company-detail">
      <Link
        to="/dashboard/companies"
        className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="back-to-companies"
      >
        <ArrowLeft className="h-4 w-4" /> Back to companies
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {renaming ? (
              <form
                onSubmit={saveRename}
                className="flex items-center gap-2"
                data-testid="company-rename-form"
              >
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") cancelRename(); }}
                  placeholder={data.is_name_placeholder ? "Enter real company name…" : "Company name"}
                  disabled={renameSaving}
                  className="text-lg font-semibold w-80"
                  data-testid="company-rename-input"
                  maxLength={200}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={renameSaving || !renameValue.trim()}
                  data-testid="company-rename-save"
                >
                  {renameSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={cancelRename}
                  disabled={renameSaving}
                  data-testid="company-rename-cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <>
                <h1 className="text-2xl font-heading font-bold text-slate-900" data-testid="company-detail-name">
                  {data.company}
                </h1>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={startRename}
                    data-testid="company-rename-btn"
                    title={data.is_name_placeholder ? "Add a real company name" : "Rename company"}
                    className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
              </>
            )}
            {data.hot_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
                <Flame className="h-3 w-3" /> {data.hot_count} hot
              </span>
            )}
            {data.is_name_placeholder && !renaming && (
              <button
                type="button"
                onClick={startRename}
                data-testid="company-detail-placeholder-pill"
                disabled={readOnly}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                title="This company was auto-created from an account number. Click to add the real name."
              >
                <AlertCircle className="h-3 w-3" />
                Needs company name
              </button>
            )}
          </div>
          <div className="mt-2 text-sm text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{data.primary_contact || "—"}</span>
            {data.primary_email && (
              <a href={`mailto:${data.primary_email}`} className="inline-flex items-center gap-1 hover:text-slate-900">
                <Mail className="h-3.5 w-3.5" /> {data.primary_email}
              </a>
            )}
            {data.primary_phone && (
              <a href={`tel:${data.primary_phone}`} className="inline-flex items-center gap-1 hover:text-slate-900">
                <Phone className="h-3.5 w-3.5" /> {data.primary_phone}
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full border ${STAGE_BADGE[data.dominant_stage] || "bg-slate-100"}`}>
            {STAGE_LABEL[data.dominant_stage] || data.dominant_stage}
          </span>
          {user?.role === "admin" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMergeOpen(true)}
              data-testid="company-merge-btn"
            >
              <GitMerge className="h-3.5 w-3.5 mr-1.5" /> Merge
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Submissions</div>
          <div className="text-2xl font-heading font-bold text-slate-900 mt-1" data-testid="company-stat-submissions">
            {data.submission_count}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Consultations</div>
          <div className="text-2xl font-heading font-bold text-slate-900 mt-1">
            {data.consultation_count}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Won deals</div>
          <div className="text-2xl font-heading font-bold text-emerald-700 mt-1">
            {data.won_count}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Pipeline $</div>
          <div className="text-2xl font-heading font-bold text-slate-900 mt-1" data-testid="company-stat-pipeline">
            {formatMoney(data.estimated_value)}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Timeline */}
        <Card className="p-5 lg:col-span-2">
          <h2 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <History className="h-4 w-4" /> Unified timeline
          </h2>
          {data.timeline.length === 0 ? (
            <p className="text-sm text-slate-500">No activity yet.</p>
          ) : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="company-timeline">
              {data.timeline.map((e, i) => {
                const Icon = KIND_ICON[e.kind] || FileText;
                // Stable composite key — timeline is a merge of submissions +
                // notes + audit rows that share no natural ID, but (kind+ts+
                // submission_id) is unique in practice.
                const stableKey = `${e.kind}-${e.ts || i}-${e.submission_id || ""}-${i}`;
                return (
                  <li key={stableKey} className="ml-4">
                    <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-white border border-slate-300" />
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Icon className="h-3.5 w-3.5" />
                          <span className="uppercase tracking-wider font-medium">
                            {KIND_LABEL[e.kind] || e.kind}
                          </span>
                          {e.kind === "submission" && e.formType && (
                            <span className="text-slate-400">• {formatFormType(e.formType)}</span>
                          )}
                          {e.kind === "audit" && e.action && (
                            <span className="text-slate-400">• {e.action.replace(/_/g, " ")}</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-sm text-slate-900">
                          {e.kind === "submission" && (
                            <Link
                              to={`/dashboard/submissions/${e.submission_id}`}
                              className="hover:underline"
                            >
                              {e.summary || "(no subject)"}
                              {e.is_hot && <Flame className="h-3 w-3 text-rose-500 inline ml-1" />}
                            </Link>
                          )}
                          {e.kind === "note" && (
                            <span>{e.text}</span>
                          )}
                          {e.kind === "audit" && (
                            <span className="text-slate-700">
                              {e.payload?.from && e.payload?.to
                                ? `${STAGE_LABEL[e.payload.from] || e.payload.from} → ${STAGE_LABEL[e.payload.to] || e.payload.to}`
                                : JSON.stringify(e.payload || {})}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {e.actor || "—"} • {formatRelative(e.ts)}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>

        {/* Submissions list */}
        <Card className="p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4" /> Submissions ({data.submission_count})
          </h2>
          {data.submissions.length === 0 ? (
            <p className="text-sm text-slate-500">No submissions.</p>
          ) : (
            <ul className="space-y-3">
              {data.submissions.map((s) => (
                <li key={s.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <Link
                    to={`/dashboard/submissions/${s.id}`}
                    className="block group"
                    data-testid={`company-submission-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900 group-hover:underline truncate">
                        {formatFormType(s.formType)}
                      </span>
                      <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${STAGE_BADGE[s.status] || "bg-slate-100"}`}>
                        {STAGE_LABEL[s.status] || s.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {s.name || s.email || "—"}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDateTime(s.created_at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <MergeCompanyDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        source={{ id: data.id, company: data.company }}
        onMerged={(res) => navigate(`/dashboard/companies/${res.target_slug || res.target_id}`)}
      />

      {renameConflict && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="rename-conflict-dialog"
          onClick={() => !renameSaving && setRenameConflict(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <GitMerge className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-slate-900">
                  A company by that name already exists
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {renameConflict.message}{" "}
                  It has <span className="font-medium">
                    {renameConflict.target.target_submission_count}
                  </span>{" "}
                  linked submission{renameConflict.target.target_submission_count === 1 ? "" : "s"}.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Merge this record into it, or rename anyway if these are
                  legitimately separate divisions.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setRenameConflict(null)}
                disabled={renameSaving}
                data-testid="rename-conflict-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => saveRename(e, { force: true })}
                disabled={renameSaving}
                data-testid="rename-conflict-force"
              >
                {renameSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Rename anyway
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={mergeIntoConflictTarget}
                disabled={renameSaving}
                data-testid="rename-conflict-merge"
              >
                {renameSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
                Merge into “{renameConflict.target.target_name}”
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
